import { ApiError } from '@/models/errors/ApiError';
import { apiRequest } from './apiClient';

export interface ImpersonatedCreditor {
    id: number;
    name: string;
}

export interface StartImpersonationResponse {
    message: string;
    creditorId: number;
}

export interface StopImpersonationResponse {
    message: string;
}

type ImpersonationApiResponse = {
    data: {
        id: number;
        name: string;
    } | null;
};

type StartImpersonationApiResponse = {
    message: string;
    creditor_id: number;
};

type StopImpersonationApiResponse = {
    message: string;
};

/**
 * GET /api/superadmin/impersonate — current impersonation from the HTTP-only cookie.
 * Returns `null` when no impersonation cookie is present.
 */
export async function getImpersonation(): Promise<ImpersonatedCreditor | null> {
    const response = await apiRequest<ImpersonationApiResponse>(
        '/api/superadmin/impersonate',
        'GET',
    );

    if (!response.data) {
        return null;
    }

    return {
        id: response.data.id,
        name: response.data.name,
    };
}

/**
 * POST /api/superadmin/impersonate — set the impersonated creditor cookie.
 */
export async function startImpersonation(
    creditorId: number,
): Promise<StartImpersonationResponse> {
    try {
        const response = await apiRequest<StartImpersonationApiResponse>(
            '/api/superadmin/impersonate',
            'POST',
            {
                body: {
                    creditor_id: creditorId,
                },
            },
        );

        return {
            message: response.message,
            creditorId: response.creditor_id,
        };
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                creditor_id: 'creditorId',
            });
        }

        throw error;
    }
}

/**
 * DELETE /api/superadmin/impersonate — clear the impersonated creditor cookie.
 */
export async function stopImpersonation(): Promise<StopImpersonationResponse> {
    const response = await apiRequest<StopImpersonationApiResponse>(
        '/api/superadmin/impersonate',
        'DELETE',
    );

    return {
        message: response.message,
    };
}
